/**
 * Notification Bell Component
 *
 * Header bell icon with unread count badge and dropdown panel.
 * Shows recent notifications with mark as read functionality.
 *
 * @module shared/components/ui/notification-bell
 */

import {
  Bell,
  Check,
  Calendar,
  CheckSquare,
  Camera,
  Heart,
  MessageCircle,
  Settings,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyScopedLoad } from '@/shared/hooks/useFamilyScopedLoad';
import { cn } from '@/shared/lib/utils';
import {
  useNotificationStore,
  useRecentNotifications,
  selectUnreadCount,
  sortNotificationsUnreadFirst,
} from '@/shared/stores/notificationStore';
import { BadgeVariant } from '@/shared/types/badge.types';
import { ButtonVariant, ButtonSize } from '@/shared/types/button.types';
import type {
  Notification,
  NotificationType,
} from '@/shared/types/notification.types';
import { Badge } from './badge';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { ScrollArea } from './scroll-area';
import { Separator } from './separator';
import { TruncatedText } from './truncated-text';

// ============================================
// Helper Components
// ============================================

/**
 * Icon mapping for notification types
 */
const NOTIFICATION_ICONS: Record<NotificationType, typeof Bell> = {
  event: Calendar,
  todo: CheckSquare,
  album: Camera,
  chat: MessageCircle,
  system: Settings,
  love: Heart,
};

/**
 * Format timestamp to relative time in Bulgarian
 */
function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return 'сега';
  if (diffMinutes < 60) return `${diffMinutes} мин`;
  if (diffHours < 24) return `${diffHours} ч`;
  if (diffDays === 1) return 'вчера';
  if (diffDays < 7) return `${diffDays} дни`;

  return date.toLocaleDateString('bg-BG', { day: 'numeric', month: 'short' });
}

// ============================================
// Notification Item
// ============================================

interface NotificationItemProps {
  readonly notification: Notification;
  readonly onMarkAsRead: (id: string) => Promise<void>;
  readonly onDelete: (id: string) => Promise<void>;
  readonly onClick: (notification: Notification) => void;
}

function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  onClick,
}: NotificationItemProps) {
  const Icon = NOTIFICATION_ICONS[notification.type];
  const expired = notification.expired === true;

  return (
    <div
      className={cn(
        'group relative flex gap-3 rounded-md p-3 transition-colors',
        expired
          ? 'cursor-default opacity-60'
          : 'cursor-pointer hover:bg-accent',
        !notification.isRead && !expired && 'bg-accent/50'
      )}
      onClick={() => {
        if (!expired) onClick(notification);
      }}
      onKeyDown={(e) => {
        if (!expired && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick(notification);
        }
      }}
      role="button"
      tabIndex={0}
      aria-disabled={expired || undefined}
    >
      {/* Icon */}
      <div
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-full',
          'bg-muted text-muted-foreground'
        )}
      >
        <Icon className="size-4" />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium leading-tight">
            {notification.title}
          </p>
          <span className="shrink-0 text-xs text-muted-foreground">
            {formatRelativeTime(notification.timestamp)}
          </span>
        </div>
        <TruncatedText
          lines={2}
          className="mt-0.5 text-xs text-muted-foreground"
        >
          {notification.message}
        </TruncatedText>
        {expired && (
          <Badge variant={BadgeVariant.Outline} className="mt-1.5">
            Приключил
          </Badge>
        )}
      </div>

      {/* Unread indicator */}
      {!notification.isRead && !expired && (
        <div className="absolute right-2 top-2 size-2 rounded-full bg-primary" />
      )}

      {/* Actions (visible on hover) */}
      <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        {!notification.isRead && (
          <Button
            variant={ButtonVariant.Ghost}
            size={ButtonSize.Icon}
            className="size-6"
            onClick={(e) => {
              e.stopPropagation();
              void onMarkAsRead(notification.id);
            }}
            aria-label="Маркирай като прочетено"
          >
            <Check className="size-3" />
          </Button>
        )}
        <Button
          variant={ButtonVariant.Ghost}
          size={ButtonSize.Icon}
          className="size-6 text-muted-foreground hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            void onDelete(notification.id);
          }}
          aria-label="Изтрий известие"
        >
          <X className="size-3" />
        </Button>
      </div>
    </div>
  );
}

// ============================================
// Empty State
// ============================================

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <Bell className="size-10 text-muted-foreground/50" />
      <p className="mt-2 text-sm font-medium text-muted-foreground">
        Няма известия
      </p>
      <p className="text-xs text-muted-foreground/70">
        Ще ви уведомим за нови събития
      </p>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

interface NotificationBellProps {
  /**
   * Optional validator for chat-room notifications. Returns whether the room
   * still exists. When the room is gone the invite is marked expired instead
   * of navigating to a dead session. When omitted, chat notifications open
   * without validation. Injected by the app layer to keep this primitive free
   * of feature dependencies.
   */
  readonly onValidateChatRoom?: (roomCode: string) => Promise<boolean>;
}

export function NotificationBell({
  onValidateChatRoom,
}: NotificationBellProps = {}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Store state and actions
  const notifications = useRecentNotifications();
  const unreadCount = useNotificationStore(selectUnreadCount);
  const fetchNotifications = useNotificationStore((s) => s.fetchNotifications);
  const markAsRead = useNotificationStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationStore((s) => s.markAllAsRead);
  const markExpired = useNotificationStore((s) => s.markExpired);
  const deleteNotification = useNotificationStore((s) => s.deleteNotification);

  // Fetch on mount AND on every family switch.
  //
  // The bell lives in `Header`, which is part of `MainLayout` and therefore
  // never unmounts for the life of the session. A bare
  // `useEffect(..., [fetchNotifications])` was effectively a once-per-session
  // fetch, because the store action reference is stable. Switching families
  // fires the family-scoped reset, which empties the list and re-arms
  // `isLoaded`, but nothing re-ran the fetch — so the bell showed „Няма
  // известия" for the rest of the session while the new family had unread ones.
  useFamilyScopedLoad(fetchNotifications);

  // Surface unread notifications above read ones (FR17).
  const ordered = sortNotificationsUnreadFirst(notifications);

  const openNotificationLink = (notification: Notification): void => {
    if (!notification.isRead) void markAsRead(notification.id);
    if (notification.link) {
      setOpen(false);
      void navigate(notification.link);
    }
  };

  // Handle notification click. Chat invites are validated against the relay
  // first: if the room no longer exists the invite is marked expired and does
  // NOT navigate to a dead session.
  const handleNotificationClick = (notification: Notification) => {
    if (notification.expired === true) return;

    if (notification.type === 'chat' && onValidateChatRoom) {
      const roomCode =
        typeof notification.data?.['roomCode'] === 'string'
          ? notification.data['roomCode']
          : null;
      if (roomCode) {
        void onValidateChatRoom(roomCode).then((exists) => {
          if (!exists) {
            markExpired(notification.id);
            return;
          }
          openNotificationLink(notification);
        });
        return;
      }
    }

    openNotificationLink(notification);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={ButtonVariant.Ghost}
          size={ButtonSize.Icon}
          className="relative"
          aria-label={`Известия${unreadCount > 0 ? ` (${unreadCount} непрочетени)` : ''}`}
        >
          <Bell className="size-5" />

          {/* Unread count badge */}
          {unreadCount > 0 && (
            <span
              className={cn(
                'absolute right-0 top-0 flex items-center justify-center',
                'min-w-[18px] h-[18px] px-1 rounded-full',
                'bg-destructive text-destructive-foreground',
                'text-[10px] font-bold leading-none',
                'animate-in fade-in-0 zoom-in-75',
                'ring-0',
                '-translate-y-1/3 translate-x-1/3'
              )}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0 sm:w-96" sideOffset={8}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="font-semibold">Известия</h3>
          {unreadCount > 0 && (
            <Button
              variant={ButtonVariant.Ghost}
              size={ButtonSize.Small}
              className="h-7 text-xs"
              onClick={() => void markAllAsRead()}
            >
              <Check className="mr-1 size-3" />
              Прочетени
            </Button>
          )}
        </div>

        {/* Notifications list */}
        {notifications.length === 0 ? (
          <EmptyState />
        ) : (
          <ScrollArea className="max-h-80">
            <div className="divide-y divide-border">
              {ordered.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onDelete={deleteNotification}
                  onClick={handleNotificationClick}
                />
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Footer */}
        {notifications.length > 0 && (
          <>
            <Separator />
            <div className="p-2">
              <Button
                variant={ButtonVariant.Ghost}
                className="w-full justify-center text-sm"
                onClick={() => {
                  setOpen(false);
                  void navigate('/notifications');
                }}
              >
                Виж всички известия →
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
